import { useState, useEffect, useCallback } from 'react';
import { closeAttendance, reopenClosure, listClosures } from '../lib/closures';

const startOfTodayInput = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
const nowLocalInput = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
const fmtTime = (d) => new Date(d).toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const field = 'w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-300';

// Botón + ventana para cerrar la jornada. Después del cierre nadie puede sumar tiempo.
export default function CierreJornada({ employees = [], locations = [], onDone, showToast, className = '', label = 'Cerrar jornada' }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState('all');
  const [emp, setEmp] = useState('');
  const [loc, setLoc] = useState('');
  const [at, setAt] = useState(nowLocalInput());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);

  const loadList = useCallback(async () => { try { setList(await listClosures(10)); } catch (e) { /* sin historial */ } }, []);
  useEffect(() => { if (open) { setAt(nowLocalInput()); loadList(); } }, [open, loadList]);

  const people = employees.filter((e) => e.role !== 'kiosk');
  const target = scope === 'all' ? 'todo el comercio' : scope === 'location' ? `la sede ${locations.find((l) => l.id === loc)?.name || ''}` : (employees.find((e) => e.id === emp)?.name || 'la persona');
  const valid = scope === 'all' || (scope === 'employee' && emp) || (scope === 'location' && loc);

  const submit = async () => {
    if (!valid) return;
    const when = new Date(at);
    if (!window.confirm(`Vas a cerrar la jornada de ${target} a las ${when.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}.\n\nLas salidas que estén abiertas o sean posteriores a esa hora se fijan en ese momento, y nadie podrá registrar más tiempo hoy. ¿Confirmás?`)) return;
    setBusy(true);
    try {
      const r = await closeAttendance({ scope, employeeId: scope === 'employee' ? emp : null, locationId: scope === 'location' ? loc : null, closedAt: when.toISOString(), reason });
      showToast?.(`Jornada cerrada: ${r.closed} salida(s) cargada(s) y ${r.clamped} corregida(s)`);
      setReason(''); await loadList(); await onDone?.();
    } catch (e) { showToast?.(e.message || 'No se pudo cerrar la jornada', 'error'); }
    setBusy(false);
  };
  const reopen = async (c) => {
    if (!window.confirm('¿Reabrir este cierre? Las personas podrán volver a registrar su entrada hoy. Las salidas ya corregidas no se modifican.')) return;
    try { await reopenClosure(c.id); showToast?.('Cierre reabierto'); await loadList(); await onDone?.(); }
    catch (e) { showToast?.(e.message || 'Error', 'error'); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className={className || 'flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-bold text-white bg-gray-900 hover:bg-gray-800'}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 118 0v3" /></svg>
        {label}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Cerrar jornada</h2>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full text-gray-400 hover:bg-gray-100">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-500 leading-relaxed">Fijás hasta qué hora se trabajó. Las jornadas abiertas se cierran a esa hora, las salidas registradas más tarde se corrigen y <b className="text-gray-700">nadie puede sumar tiempo después</b>: tampoco marcar una entrada nueva ese día. Queda todo registrado.</p>

              <div className="flex gap-1.5 flex-wrap">
                {[['all', 'Todo el comercio'], ...(locations.length > 1 ? [['location', 'Una sede']] : []), ['employee', 'Una persona']].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setScope(k)} className={`px-3.5 py-2 rounded-xl text-xs font-bold ${scope === k ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-500'}`}>{l}</button>
                ))}
              </div>
              {scope === 'location' && (
                <select value={loc} onChange={(e) => setLoc(e.target.value)} className={field}>
                  <option value="">Elegí la sede…</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}
              {scope === 'employee' && (
                <select value={emp} onChange={(e) => setEmp(e.target.value)} className={field}>
                  <option value="">Elegí la persona…</option>
                  {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}

              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1">Hora de cierre</label>
                <input type="datetime-local" value={at} min={startOfTodayInput()} max={nowLocalInput()} onChange={(e) => setAt(e.target.value)} className={field} />
                <p className="text-[11px] text-gray-400 mt-1">Por defecto, ahora. Tiene que ser de hoy. Si el comercio cerró antes, poné la hora real: lo registrado después se recorta hasta ese momento.</p>
              </div>
              <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="Motivo (opcional)" className={field} />

              <button disabled={busy || !valid} onClick={submit} className="w-full py-3 rounded-2xl text-sm font-bold text-white bg-gray-900 disabled:opacity-40">
                {busy ? 'Cerrando…' : `Cerrar jornada de ${target}`}
              </button>

              {list.length > 0 && (
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2">Cierres recientes</p>
                  <div className="space-y-2">
                    {list.map((c) => (
                      <div key={c.id} className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 px-3.5 py-2.5 text-xs">
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 truncate">{c.scope === 'all' ? 'Todo el comercio' : c.scope === 'location' ? `Sede ${c.location_name || ''}` : c.employee_name} · {fmtTime(c.closed_at)}</p>
                          <p className="text-gray-400 truncate">{c.closed_count} cargada(s) · {c.clamped_count} corregida(s){c.by_name ? ` · por ${c.by_name}` : ''}{c.reason ? ` · ${c.reason}` : ''}</p>
                        </div>
                        {c.reopened_at ? <span className="text-gray-400 flex-shrink-0">Reabierto</span>
                          : <button onClick={() => reopen(c)} className="font-bold text-sky-600 flex-shrink-0">Reabrir</button>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
